import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  fetchTrainerById,
  updateTrainer,
  deleteTrainer,
  fetchAllBatchesSimple,
} from "../pages/services/trainerService";
import { fetchAllSubjectsSimple } from "../pages/services/courseService";
import "../styles/AddForm.css";

// ─── Toggle switch ────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
  <div
    onClick={onChange}
    style={{
      position: "relative", width: 44, height: 24, borderRadius: 12, cursor: "pointer",
      background: checked ? "#6d28d9" : "#cbd5e1",
      transition: "background 0.2s", flexShrink: 0,
    }}
  >
    <div style={{
      position: "absolute", top: 2, left: checked ? 22 : 2,
      width: 20, height: 20, borderRadius: "50%", background: "#fff",
      transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
    }} />
  </div>
);

// ─── Section label ────────────────────────────────────────────────────────────
const Field = ({ label, required, error, children, hint }) => (
  <div className="af-field">
    <label className="af-label">
      {label}{required && <span className="af-required">*</span>}
    </label>
    {children}
    {hint  && <span style={{ fontSize: 12, color: "#64748b", marginTop: 4, display: "block" }}>{hint}</span>}
    {error && <span className="af-error">{error}</span>}
  </div>
);

// ─── Multi-assign selector with "Allot All" ───────────────────────────────────
const AssignSelector = ({ items, selectedIds, onToggle, onSetAll, searchPlaceholder, renderLabel, renderBadge }) => {
  const [search, setSearch] = useState("");
  const filtered      = items.filter(item => renderLabel(item).toLowerCase().includes(search.toLowerCase()));
  const selectedItems = items.filter(i => selectedIds.includes(i._id));
  const allSelected   = items.length > 0 && items.every(i => selectedIds.includes(i._id));

  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", background: "#fff" }}>

      {/* ── Allot All row ── */}
      <div
        onClick={() => onSetAll(allSelected ? [] : items.map(i => i._id))}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", borderBottom: "1px solid #e2e8f0",
          cursor: "pointer", background: allSelected ? "#f5f3ff" : "#fafaf9",
        }}
      >
        <input
          type="checkbox"
          checked={allSelected}
          onChange={() => {}}
          style={{ accentColor: "#6d28d9", cursor: "pointer", flexShrink: 0 }}
        />
        <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", flex: 1 }}>Allot All</span>
        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 500 }}>
          {selectedIds.length} / {items.length} selected
        </span>
      </div>

      {/* ── Selected chips ── */}
      {selectedItems.length > 0 && (
        <div style={{ padding: "10px 12px", borderBottom: "1px solid #e2e8f0", display: "flex", flexWrap: "wrap", gap: 6 }}>
          {selectedItems.map(item => (
            <span key={item._id} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "#ede9fe", color: "#6d28d9", borderRadius: 20,
              padding: "3px 10px 3px 12px", fontSize: 13, fontWeight: 600,
            }}>
              {renderLabel(item)}
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onToggle(item._id); }}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "#7c3aed", fontWeight: 700, fontSize: 15, lineHeight: 1, padding: "0 2px",
                }}
              >×</button>
            </span>
          ))}
        </div>
      )}

      {/* ── Search ── */}
      <div style={{ padding: "8px 12px", borderBottom: "1px solid #f1f5f9" }}>
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: "100%", border: "none", outline: "none", fontSize: 13, color: "#334155", background: "transparent" }}
        />
      </div>

      {/* ── Item list ── */}
      <div style={{ maxHeight: 220, overflowY: "auto" }}>
        {filtered.length === 0 ? (
          <p style={{ padding: "14px 16px", fontSize: 13, color: "#94a3b8", textAlign: "center" }}>No items found</p>
        ) : filtered.map(item => {
          const selected = selectedIds.includes(item._id);
          return (
            <div
              key={item._id}
              onClick={() => onToggle(item._id)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 14px", cursor: "pointer",
                background: selected ? "#f5f3ff" : "transparent",
                borderBottom: "1px solid #f8fafc", transition: "background 0.1s",
              }}
            >
              <input type="checkbox" checked={selected} readOnly
                style={{ accentColor: "#6d28d9", cursor: "pointer", flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "#1e293b", flex: 1, fontWeight: selected ? 600 : 400 }}>
                {renderLabel(item)}
              </span>
              {renderBadge && renderBadge(item)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const UpdateTrainer = () => {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const location  = useLocation();

  const [formData,  setFormData]  = useState({ trainerName: "", contactNumber: "" });
  const [errors,    setErrors]    = useState({});
  const [errorMsg,  setErrorMsg]  = useState("");
  const [loading,   setLoading]   = useState(false);
  const [fetching,  setFetching]  = useState(false);

  const [allBatches,       setAllBatches]       = useState([]);
  const [allSubjects,      setAllSubjects]       = useState([]);
  const [selBatches,       setSelBatches]        = useState([]);
  const [selSubjects,      setSelSubjects]       = useState([]);
  const [isActive,         setIsActive]          = useState(true);
  const [trackingEnabled,  setTrackingEnabled]   = useState(true);

  const [deleteConfirm,   setDeleteConfirm]   = useState(false);
  const [deleteLoading,   setDeleteLoading]   = useState(false);
  const [trainerName,     setTrainerNameCache] = useState("");

  useEffect(() => {
    setFetching(true);

    const loadTrainer = location.state?.trainer
      ? Promise.resolve(location.state.trainer)
      : fetchTrainerById(id);

    Promise.all([loadTrainer, fetchAllBatchesSimple(), fetchAllSubjectsSimple()])
      .then(([trainer, batches, subjects]) => {
        setFormData({
          trainerName:   trainer.trainerName   || "",
          contactNumber: trainer.contactNumber ? String(trainer.contactNumber) : "",
        });
        setTrainerNameCache(trainer.trainerName || "");
        setIsActive(trainer.isActive !== false);
        setTrackingEnabled(trainer.trackingEnabled !== false);
        setSelBatches((trainer.assignedBatches  || []).map(x => x.toString()));
        setSelSubjects((trainer.assignedSubjects || []).map(x => x.toString()));
        setAllBatches((batches   || []).map(b => ({ ...b, _id: b._id.toString() })));
        setAllSubjects((subjects || []).map(s => ({ ...s, _id: s._id.toString() })));
      })
      .catch(() => navigate("/trainers"))
      .finally(() => setFetching(false));
  }, [id, location.state, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    setErrors(p  => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.trainerName.trim()) errs.trainerName = "Trainer name is required";
    if (formData.contactNumber && !/^\d{10}$/.test(formData.contactNumber.trim()))
      errs.contactNumber = "Enter a valid 10-digit mobile number";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setErrorMsg("");
    try {
      await updateTrainer(id, {
        trainerName:     formData.trainerName.trim(),
        contactNumber:   formData.contactNumber.trim() || null,
        isActive,
        trackingEnabled,
        assignedBatches:  selBatches,
        assignedSubjects: selSubjects,
      });
      navigate(`/trainers/${id}`, { state: { successMessage: "Trainer updated successfully!" } });
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteTrainer(id);
      navigate("/trainers", { state: { successMessage: `Trainer "${trainerName}" removed.` } });
    } catch {
      setErrorMsg("Failed to delete trainer. Please try again.");
      setDeleteConfirm(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const statusBadge = (status) => {
    const colors = {
      Active:    { bg: "#dcfce7", color: "#15803d" },
      Inactive:  { bg: "#fef9c3", color: "#854d0e" },
      Completed: { bg: "#f1f5f9", color: "#64748b" },
    };
    const c = colors[status] || colors.Inactive;
    return (
      <span style={{
        fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
        textTransform: "uppercase", letterSpacing: 0.4,
        background: c.bg, color: c.color, flexShrink: 0,
      }}>
        {status}
      </span>
    );
  };

  if (fetching) {
    return (
      <div className="af-page">
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Loading trainer…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="af-page">
      {loading && (
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Saving changes…</p>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999,
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, padding: "32px 28px", maxWidth: 380, width: "90%",
            textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🗑️</div>
            <h3 style={{ margin: "0 0 8px", color: "#1e293b", fontSize: 18 }}>Delete Trainer?</h3>
            <p style={{ color: "#64748b", fontSize: 14, margin: "0 0 24px" }}>
              <strong>{trainerName}</strong> will be permanently removed. This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                style={{
                  padding: "9px 22px", borderRadius: 8, border: "1px solid #e2e8f0",
                  background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14,
                }}
                onClick={() => setDeleteConfirm(false)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: "9px 22px", borderRadius: 8, border: "none",
                  background: "#dc2626", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 14,
                }}
                onClick={handleDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting…" : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="af-card">
        <div className="af-header">
          <div className="af-header-icon violet">✏️</div>
          <div className="af-header-text">
            <h2 className="af-title">Edit Trainer</h2>
            <p className="af-subtitle">Update profile, allot batches &amp; topics, manage access</p>
          </div>
        </div>

        {errorMsg && (
          <div className="af-alert af-alert-error" style={{ margin: "16px 32px 0" }}>{errorMsg}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="af-body">

            {/* ── 1. Profile Info ── */}
            <div className="af-section">
              <p className="af-section-title">Trainer Information</p>

              <Field label="Full Name" required error={errors.trainerName}>
                <input
                  type="text"
                  name="trainerName"
                  placeholder="e.g. Priya Sharma"
                  value={formData.trainerName}
                  onChange={handleChange}
                  className={errors.trainerName ? "error-input" : ""}
                />
              </Field>

              <Field
                label="WhatsApp / Contact Number"
                error={errors.contactNumber}
                hint="The trainer uses this number to log in to the Trainer Portal via OTP."
              >
                <input
                  type="tel"
                  name="contactNumber"
                  placeholder="10-digit mobile number"
                  value={formData.contactNumber}
                  onChange={handleChange}
                  className={errors.contactNumber ? "error-input" : ""}
                  maxLength={10}
                />
              </Field>
            </div>

            {/* ── 2. Teaching Batches ── */}
            <div className="af-section">
              <p className="af-section-title">Teaching Batches</p>
              <p style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
                Batches this trainer is assigned to teach.
                {selBatches.length > 0 && (
                  <strong style={{ color: "#6d28d9" }}> {selBatches.length} selected.</strong>
                )}
              </p>
              {allBatches.length === 0 ? (
                <p style={{ fontSize: 13, color: "#94a3b8" }}>No batches available. Create batches first.</p>
              ) : (
                <AssignSelector
                  items={allBatches}
                  selectedIds={selBatches}
                  onToggle={id => setSelBatches(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                  onSetAll={ids => setSelBatches(ids)}
                  searchPlaceholder="Search batches…"
                  renderLabel={b => b.batchTitle}
                  renderBadge={b => statusBadge(b.batchStatus)}
                />
              )}
            </div>

            {/* ── 3. Learning Topics ── */}
            <div className="af-section">
              <p className="af-section-title">Learning Topics</p>
              <p style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
                Subjects this trainer can view and study in the Trainer Portal.
                {selSubjects.length > 0 && (
                  <strong style={{ color: "#6d28d9" }}> {selSubjects.length} selected.</strong>
                )}
              </p>
              {allSubjects.length === 0 ? (
                <p style={{ fontSize: 13, color: "#94a3b8" }}>No subjects available. Create subjects first.</p>
              ) : (
                <AssignSelector
                  items={allSubjects}
                  selectedIds={selSubjects}
                  onToggle={id => setSelSubjects(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                  onSetAll={ids => setSelSubjects(ids)}
                  searchPlaceholder="Search subjects…"
                  renderLabel={s => s.title}
                />
              )}
            </div>

            {/* ── 4. Account Settings ── */}
            <div className="af-section">
              <p className="af-section-title">Account Settings</p>

              {/* Account Active toggle */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 16px", background: "#f8fafc", borderRadius: 10,
                border: "1px solid #e2e8f0", marginBottom: 10,
              }}>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#1e293b" }}>
                    Account Active
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
                    {isActive
                      ? "Trainer can log in to the Trainer Portal."
                      : "Trainer is blocked from logging in."}
                  </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{
                    fontSize: 12, fontWeight: 700, color: isActive ? "#15803d" : "#dc2626",
                  }}>
                    {isActive ? "Enabled" : "Disabled"}
                  </span>
                  <Toggle checked={isActive} onChange={() => setIsActive(p => !p)} />
                </div>
              </div>

              {/* Activity Tracking toggle */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 16px", background: "#f8fafc", borderRadius: 10,
                border: "1px solid #e2e8f0",
              }}>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#1e293b" }}>
                    Activity Tracking
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
                    {trackingEnabled
                      ? "Trainer activity (logins, page visits, etc.) is being recorded."
                      : "No activity will be logged for this trainer."}
                  </p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{
                    fontSize: 12, fontWeight: 700, color: trackingEnabled ? "#6d28d9" : "#64748b",
                  }}>
                    {trackingEnabled ? "ON" : "OFF"}
                  </span>
                  <Toggle checked={trackingEnabled} onChange={() => setTrackingEnabled(p => !p)} />
                </div>
              </div>
            </div>

          </div>

          {/* ── Save / Cancel ── */}
          <div className="af-submit-row" style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button
              type="button"
              className="af-cancel-btn"
              onClick={() => navigate(`/trainers/${id}`)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="af-submit"
              style={{ width: "auto", flex: 1 }}
              disabled={loading}
            >
              {loading ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>

        {/* ── Danger Zone ── */}
        <div style={{
          margin: "0 32px 32px",
          padding: "20px",
          border: "1px solid #fecaca",
          borderRadius: 12,
          background: "#fff5f5",
        }}>
          <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "#dc2626" }}>Danger Zone</p>
          <p style={{ margin: "0 0 14px", fontSize: 13, color: "#64748b" }}>
            Permanently delete this trainer and all their data. This action cannot be undone.
          </p>
          <button
            type="button"
            onClick={() => setDeleteConfirm(true)}
            style={{
              background: "#dc2626", color: "#fff", border: "none",
              borderRadius: 8, padding: "9px 20px", fontSize: 13, fontWeight: 700,
              cursor: "pointer",
            }}
          >
            🗑️ Delete Trainer
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateTrainer;
